import { ANALYSIS_CARD_KINDS, ANALYSIS_OP_CARDS, ANALYSIS_TOOL_CARDS } from './analysis.js';
import type { AnalysisAgentCard, AnalysisCardLiveData } from './analysis.js';
import { CAMPAIGN_CARD_KINDS, CAMPAIGN_OP_CARDS, CAMPAIGN_TOOL_CARDS } from './campaign.js';
import type { CampaignAgentCard, CampaignCardLiveData } from './campaign.js';
import { CORE_CARD_KINDS, CORE_TOOL_CARDS } from './core.js';
import type { CoreAgentCard } from './core.js';
import { CREATIVE_CARD_KINDS, CREATIVE_OP_CARDS, CREATIVE_TOOL_CARDS } from './creative.js';
import type { CreativeAgentCard, CreativeCardLiveData } from './creative.js';
import { ENGAGEMENT_CARD_KINDS, ENGAGEMENT_OP_CARDS, ENGAGEMENT_TOOL_CARDS } from './engagement.js';
import type { EngagementAgentCard, EngagementCardLiveData } from './engagement.js';
import { FILES_CARD_KINDS, FILES_TOOL_CARDS } from './files.js';
import type { FilesAgentCard, FilesCardLiveData } from './files.js';
import { OPS_CARD_KINDS, OPS_OP_CARDS, OPS_TOOL_CARDS } from './ops.js';
import type { OpsAgentCard, OpsCardLiveData } from './ops.js';
import { opIdOfToolName, opMatches, type OpCardRoute } from './routes.js';

export * from './actions.js';
export * from './routes.js';
/* Kart gösterim politikası — `present` alanı, tür sınıfları (1 Eki 2026). */
export * from './presentation.js';
export { AGENT_TABLE_IDS, CORE_CARD_KINDS, CORE_TOOL_CARDS } from './core.js';
export type { AgentTableFormat, AgentTableId, CoreAgentCard } from './core.js';
export { ANALYSIS_CARD_KINDS, ANALYSIS_OP_CARDS, ANALYSIS_TOOL_CARDS, BREAKDOWN_DIMENSIONS } from './analysis.js';
export type {
  AnalysisAgentCard,
  AnalysisCardLiveData,
  AnalysisSourceStatus,
  BreakdownDimension,
  CompareSide,
  ExperimentItem,
  MetricsCardExtras,
} from './analysis.js';
/* Kampanya alanı: türler + saf yardımcılar (`campaignPlanOf`, takvim ızgarası). */
export * from './campaign.js';
export {
  CREATIVE_CARD_KINDS,
  CREATIVE_LIVE_CARD_TOOLS,
  CREATIVE_LIVE_TOOL_NAME,
  CREATIVE_OP_CARDS,
  CREATIVE_TOOL_CARDS,
  LOGO_CANDIDATE_SOURCES,
  PRODUCTION_CARD_VIEWS,
  VARIANT_WIN_METRICS,
  creativeLiveCallId,
  creativeLiveCardKindOf,
} from './creative.js';
export type {
  CreativeAgentCard,
  CreativeCardExtras,
  CreativeCardLiveData,
  CreativeJobSubject,
  CreativeJobType,
  CreativeLiveCardTool,
  LogoCandidateSource,
  ProductionCardView,
  VariantWinMetric,
} from './creative.js';
export {
  COMMENT_CARD_SURFACES,
  COMMENT_UNAVAILABLE_CODES,
  ENGAGEMENT_CARD_KINDS,
  ENGAGEMENT_OP_CARDS,
  ENGAGEMENT_TOOL_CARDS,
} from './engagement.js';
/* Faz 9 X1 — AI'ın ürettiği dosya (kod çalıştırma / beceri → R2 → kart). */
export { FILES_CARD_KINDS, FILES_TOOL_CARDS, FILE_OUTPUT_TOOL_NAME, fileOutputCallId } from './files.js';
export type { FilesAgentCard, FilesCardLiveData } from './files.js';
export type {
  CommentCardSurface,
  CommentSentiment,
  CommentUnavailableCode,
  EngagementAgentCard,
  EngagementCardLiveData,
} from './engagement.js';
export {
  ACTIVITY_ACTION_CODES,
  ACTIVITY_NO_UNDO_CODES,
  BALANCE_NOTE_CODES,
  BALANCE_STATES,
  NEEDS_ACTION_CATEGORIES,
  NEEDS_ACTION_STEP_CODES,
  OPS_CARD_KINDS,
  OPS_OP_CARDS,
  OPS_TOOL_CARDS,
  TASK_RESULT_OUTCOMES,
  needsActionFromFailure,
} from './ops.js';
export type {
  DisconnectImpactView,
  ActivityActionCode,
  ActivityNoUndoCode,
  BalanceNoteCode,
  BalanceState,
  NeedsActionCategory,
  NeedsActionInput,
  NeedsActionStepCode,
  OpsAgentCard,
  OpsCardLiveData,
  TaskResultOutcome,
} from './ops.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SOHBET KARTLARI — `AgentCard` birliği, alan dosyalarına bölünmüş (Faz 8)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bir aracın sohbette çizilen hâli. Kart bir aracın SONUCU: `tool_result`
 * `toolCallId` taşıyor ve kart o kimliğin altına oturuyor — sohbet satırına
 * `toolCalls[].ui` olarak yazılıyor, yenilemede geri geliyor (ayrı bir SSE
 * karesi değil; gerekçe `core.ts` başındaki eski notta).
 *
 * `ui` ≠ `result`: `result` modelin gördüğü ham gövde (satırda, yanıtta
 * KIRPILIYOR), `ui` ekranın gördüğü DAR zarf (saklanıyor ve dönüyor).
 *
 * Dosyalar ve sahipleri:
 *
 *   core.ts      altı çekirdek tür (ask, product, creative, metrics, table,
 *                connect) — altyapı. `metrics`/`creative` uzantısı alan
 *                dosyasında (`MetricsCardExtras`, `CreativeCardExtras`).
 *   analysis.ts  A · analiz        campaign.ts  B · kampanya kurma
 *   creative.ts  C · kreatif       ops.ts       E · operasyon ve hesap
 *   engagement.ts D · yorum, lead, politika, dönüşüm takibi (30 Eyl 2026'da
 *                creative/ops'tan taşındı — kişisel veri kuralları ortak)
 *   files.ts     X1 · AI'ın ürettiği dosya (Faz 9 — kod çalıştırma/beceri)
 *   actions.ts   kart eylemi sözleşmesi (düğme = insan kararı)
 *   routes.ts    platform işlemi → tür deseni
 *   presentation.ts  kart NE ZAMAN çizilir — `present` alanı + tür sınıfı
 *                (always / shown / pick / on_request); yeni tür oraya da
 *                yazılır (tam tipli sözlük, eksikse derleme kırılır)
 *
 * ───────────────────────────────────────────────────────────────────────────
 * YENİ TÜR EKLEMEK (alan ajanı yalnız KENDİ dosyalarına dokunur)
 * ───────────────────────────────────────────────────────────────────────────
 *   1. shared `agent-cards/<alan>.ts` → birliğe dal `Actionable<{ kind: '<ad>';
 *      … }>` + `<ALAN>_CARD_KINDS`e ad. Dar zarf: sır, jeton, imzalı adres,
 *      KİŞİSEL VERİ girmez (kimlik ve sayı girer; görüntü verisi canlı uçtan
 *      → `<Alan>CardLiveData['<ad>']`).
 *   2. Kaynağı bağla:
 *        · katalog okuma işlemi → `<ALAN>_OP_CARDS`a `{ match, kind }` +
 *          API `apps/api/src/agent/cards/<alan>/index.ts`e aynı `match` ile
 *          üretici `(opId, params, result, ctx) => kart | null`;
 *        · ekran aracı → `app-tools/*.tools.ts` `card`;
 *        · çekirdek araç → `ToolOutcome.ui`.
 *      Araç adıyla çizilen kart için `<ALAN>_TOOL_CARDS`a satır.
 *   3. Web `components/dashboard/agent/tool-renderers/<alan>.tsx` → alan
 *      sözlüğüne `<ad>: { view }`. Sözlük alanın türlerine TAM tipli: dal eklenip
 *      görünüm yazılmazsa derleme kırılır. Görünüm `CardShell` içinde.
 *   4. Metinler `node scripts/i18n/add-messages.mjs parca.json` (en+tr):
 *      başlık `dashboard.agentChat.cards.kind.<ad>` ZORUNLU (test denetliyor).
 *   4b. Gösterim sınıfı: `./presentation.ts` → `CARD_PRESENTATION`e satır
 *      (sunum kartı `shown`, seçici `pick`, kullanıcıdan bir şey isteyen
 *      `always`). Okuma aracının kartı `present` alanıyla açılıp kapanır.
 *   5. Düğme gerekiyorsa kartın `actions`ı (`./actions.ts`) — sunucu yürütür,
 *      model değil. Kullanıcıdan cevap bekleyen kart `reply` eylemi kullanır;
 *      `cardReplySchema`ya yeni dal AÇILMAZ.
 *
 * Onay kartları (`AgentProposal` — `platform_op`, `app_op`, taslaklar) bu
 * birliğin DIŞINDA ve kendi yolunda (`agent-proposal.tsx`); `campaign_plan`
 * yalnız onların görünüm katmanı olabilir.
 */
export type AgentCard =
  | CoreAgentCard
  | AnalysisAgentCard
  | CampaignAgentCard
  | CreativeAgentCard
  | OpsAgentCard
  | EngagementAgentCard
  | FilesAgentCard;
export type AgentCardKind = AgentCard['kind'];

/** Tüm türler — çalışma anında tanıma (eski/yeni sürüm arası kart). */
export const AGENT_CARD_KINDS: readonly AgentCardKind[] = [
  ...CORE_CARD_KINDS,
  ...ANALYSIS_CARD_KINDS,
  ...CAMPAIGN_CARD_KINDS,
  ...CREATIVE_CARD_KINDS,
  ...OPS_CARD_KINDS,
  ...ENGAGEMENT_CARD_KINDS,
  ...FILES_CARD_KINDS,
];

/** Türün sahibi alan — web kayıt defteri ve testler okur. */
export type AgentCardDomain = 'core' | 'analysis' | 'campaign' | 'creative' | 'ops' | 'engagement' | 'files';
export const AGENT_CARD_DOMAINS: Readonly<Record<AgentCardDomain, readonly AgentCardKind[]>> = {
  core: CORE_CARD_KINDS,
  analysis: ANALYSIS_CARD_KINDS,
  campaign: CAMPAIGN_CARD_KINDS,
  creative: CREATIVE_CARD_KINDS,
  ops: OPS_CARD_KINDS,
  engagement: ENGAGEMENT_CARD_KINDS,
  files: FILES_CARD_KINDS,
};

export function isAgentCardKind(value: unknown): value is AgentCardKind {
  return typeof value === 'string' && (AGENT_CARD_KINDS as readonly string[]).includes(value);
}

/** Araç adı → türler (çekirdek + alanlar). Aynı araç iki alanda olamaz. */
export const AGENT_TOOL_CARDS: Readonly<Record<string, readonly AgentCardKind[]>> = {
  ...CORE_TOOL_CARDS,
  ...ANALYSIS_TOOL_CARDS,
  ...CAMPAIGN_TOOL_CARDS,
  ...CREATIVE_TOOL_CARDS,
  ...OPS_TOOL_CARDS,
  ...ENGAGEMENT_TOOL_CARDS,
  ...FILES_TOOL_CARDS,
};

/** Katalog operasyonu deseni → tür (alanlar). */
export const AGENT_OP_CARDS: readonly OpCardRoute<AgentCardKind>[] = [
  ...ANALYSIS_OP_CARDS,
  ...CAMPAIGN_OP_CARDS,
  ...CREATIVE_OP_CARDS,
  ...OPS_OP_CARDS,
  ...ENGAGEMENT_OP_CARDS,
];

/** Bu katalog operasyonunun sohbette çizebileceği türler (desen sırasıyla). */
export function opCardKinds(opId: string): AgentCardKind[] {
  const out: AgentCardKind[] = [];
  for (const route of AGENT_OP_CARDS) {
    if (opMatches(route.match, opId) && !out.includes(route.kind)) out.push(route.kind);
  }
  return out;
}

/**
 * Araç adının (çekirdek, ekran aracı ya da `opToolName` biçimli katalog
 * operasyonu) sohbette çizebileceği türler.
 */
export function toolCardKinds(name: string): AgentCardKind[] {
  const direct = AGENT_TOOL_CARDS[name];
  if (direct) return [...direct];
  const opId = opIdOfToolName(name);
  return opId ? opCardKinds(opId) : [];
}

/**
 * Canlı veri şekilleri — `GET /agent/cards/:id/data` yanıtının `data`sı,
 * türe göre. Alan dosyaları kendi parçasını tanımlar.
 */
export type AgentCardLiveData = AnalysisCardLiveData &
  CampaignCardLiveData &
  CreativeCardLiveData &
  OpsCardLiveData &
  EngagementCardLiveData &
  FilesCardLiveData;
export type AgentCardLiveKind = keyof AgentCardLiveData & AgentCardKind;

/* ── Derleme kemeri: `<ALAN>_CARD_KINDS` listesi birliğin dallarıyla BİREBİR ──
   Birliğe dal eklenip listeye yazılmazsa (ya da tersi) paket derlenmez —
   web kayıt defteri listeden türüyor ve eksik tür sessizce çizilmezdi. */
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
const kindsMatch: [
  Same<(typeof CORE_CARD_KINDS)[number], CoreAgentCard['kind']>,
  Same<(typeof ANALYSIS_CARD_KINDS)[number], AnalysisAgentCard['kind']>,
  Same<(typeof CAMPAIGN_CARD_KINDS)[number], CampaignAgentCard['kind']>,
  Same<(typeof CREATIVE_CARD_KINDS)[number], CreativeAgentCard['kind']>,
  Same<(typeof OPS_CARD_KINDS)[number], OpsAgentCard['kind']>,
  Same<(typeof ENGAGEMENT_CARD_KINDS)[number], EngagementAgentCard['kind']>,
  Same<(typeof FILES_CARD_KINDS)[number], FilesAgentCard['kind']>,
] = [true, true, true, true, true, true, true];
void kindsMatch;
